import { BANK_ACCOUNT_NAME_MAX_CHARS, checkBankAccount } from '@klever-webhook/shared';
import type { BankAccount, BankAccountProblem } from '@klever-webhook/shared';

/** The form's bank account as the API's bankAccount, or why it cannot be saved, in French. */
export type BankAccountForm = { ok: true; value: BankAccount | null } | { ok: false; error: string };

// The API's own rules (shared checkBankAccount), worded for the form. Its fields are always
// strings, so the *_not_string problems cannot occur here; they are worded all the same.
const PROBLEMS: Record<BankAccountProblem, string> = {
  iban_not_string: 'IBAN invalide.',
  iban_missing: 'Le BIC et le titulaire demandent un IBAN. Indiquez l’IBAN, ou videz les trois champs pour retirer le compte.',
  iban_shape: 'IBAN invalide : deux lettres, deux chiffres de contrôle, puis 11 à 30 lettres ou chiffres.',
  iban_checksum: 'IBAN invalide : les chiffres de contrôle ne correspondent pas. Vérifiez le numéro.',
  bic_not_string: 'BIC invalide.',
  bic_shape: 'BIC invalide : il compte 8 ou 11 caractères, par exemple GKCCBEBB.',
  account_name_not_string: 'Titulaire invalide.',
  account_name_too_long: `Le titulaire compte au plus ${BANK_ACCOUNT_NAME_MAX_CHARS} caractères.`,
};

/** The stored IBAN (no spaces, upper case) grouped by four, as people read it. */
export function formatIban(iban: string): string {
  return iban.replace(/(.{4})(?=.)/g, '$1 ');
}

/**
 * The form's three inputs as the API's bankAccount, checked with the API's rules so a mistake is
 * explained before anything is sent: null when all three are blank, which clears the account.
 * A BIC or name without an IBAN is refused rather than dropped silently.
 */
export function bankAccountFromForm(iban: string, bic: string, accountName: string): BankAccountForm {
  if (iban.trim() === '' && bic.trim() === '' && accountName.trim() === '') return { ok: true, value: null };
  const checked = checkBankAccount({ iban, bic, accountName });
  return checked.ok ? checked : { ok: false, error: PROBLEMS[checked.problem] };
}

/** The account on one line for the tenant details, or a dash when there is none. */
export function bankAccountSummary(account: BankAccount | null | undefined): string {
  if (!account) return '—';
  return [formatIban(account.iban), account.bic, account.accountName].filter(Boolean).join(' · ');
}
